/**
 * StrategicUnitsCard Component
 *
 * Displays team contributions table with weights and scores
 */

import React from 'react';
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
} from '@mui/material';
import { Groups } from '@mui/icons-material';
import { useOKRDashboardContext } from '../OKRDashboardContext';
import { CollapsibleCard } from '../shared/CollapsibleCard';
import { EmptyState } from '../shared/EmptyState';
import { getScoreColor } from '../utils/scoreUtils';
import { calculateTeamContributions } from '../utils/filterUtils';

export const StrategicUnitsCard: React.FC = () => {
    const { isDarkMode, data, sections, filteredObjectives } = useOKRDashboardContext();
    const { departments } = data;
    const { expandedSections, toggleSection } = sections;

    const teamContributions = calculateTeamContributions(departments, filteredObjectives.team);
    const activeTeams = teamContributions.filter(t => t.objectiveId);

    return (
        <CollapsibleCard
            icon={<Groups sx={{ color: '#fff', fontSize: 24 }} />}
            iconBgGradient="linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)"
            title="Strategic Units"
            subtitle="Team contribution overview"
            expanded={expandedSections.teamContributions}
            onToggle={() => toggleSection('teamContributions')}
            isDarkMode={isDarkMode}
            count={activeTeams.length}
            noPadding
        >
            {activeTeams.length === 0 ? (
                <Box sx={{ p: 3 }}>
                    <EmptyState
                        title="No Team Objectives"
                        description="Teams haven't created objectives yet"
                        isDarkMode={isDarkMode}
                        compact
                    />
                </Box>
            ) : (
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell
                                    sx={{
                                        fontWeight: 700,
                                        color: isDarkMode ? '#94a3b8' : '#64748b',
                                        borderBottom: `1px solid ${isDarkMode ? '#334155' : '#e2e8f0'}`,
                                        bgcolor: isDarkMode ? '#0f172a40' : '#f8fafc',
                                        fontSize: '0.75rem',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.05em',
                                    }}
                                >
                                    Team
                                </TableCell>
                                <TableCell
                                    align="center"
                                    sx={{
                                        fontWeight: 700,
                                        color: isDarkMode ? '#94a3b8' : '#64748b',
                                        borderBottom: `1px solid ${isDarkMode ? '#334155' : '#e2e8f0'}`,
                                        bgcolor: isDarkMode ? '#0f172a40' : '#f8fafc',
                                        fontSize: '0.75rem',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.05em',
                                    }}
                                >
                                    Weight
                                </TableCell>
                                <TableCell
                                    align="center"
                                    sx={{
                                        fontWeight: 700,
                                        color: isDarkMode ? '#94a3b8' : '#64748b',
                                        borderBottom: `1px solid ${isDarkMode ? '#334155' : '#e2e8f0'}`,
                                        bgcolor: isDarkMode ? '#0f172a40' : '#f8fafc',
                                        fontSize: '0.75rem',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.05em',
                                    }}
                                >
                                    Score
                                </TableCell>
                                <TableCell
                                    align="center"
                                    sx={{
                                        fontWeight: 700,
                                        color: isDarkMode ? '#94a3b8' : '#64748b',
                                        borderBottom: `1px solid ${isDarkMode ? '#334155' : '#e2e8f0'}`,
                                        bgcolor: isDarkMode ? '#0f172a40' : '#f8fafc',
                                        fontSize: '0.75rem',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.05em',
                                    }}
                                >
                                    Status
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {activeTeams.map((team) => {
                                const scoreColor = getScoreColor(team.score);
                                return (
                                    <TableRow
                                        key={team.id}
                                        sx={{
                                            '&:hover': {
                                                bgcolor: isDarkMode ? '#1e293b40' : '#f8fafc',
                                            },
                                        }}
                                    >
                                        <TableCell
                                            sx={{
                                                borderBottom: `1px solid ${isDarkMode ? '#334155' : '#e2e8f0'}`,
                                                py: 2,
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                <Box
                                                    sx={{
                                                        width: 40,
                                                        height: 40,
                                                        borderRadius: 2,
                                                        bgcolor: `${scoreColor}15`,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        border: `2px solid ${scoreColor}25`,
                                                    }}
                                                >
                                                    <Groups sx={{ color: scoreColor, fontSize: 24 }} />
                                                </Box>
                                                <Box>
                                                    <Typography
                                                        sx={{
                                                            fontWeight: 600,
                                                            fontSize: '0.9rem',
                                                            color: isDarkMode ? '#f1f5f9' : '#1e293b',
                                                        }}
                                                    >
                                                        {team.name}
                                                    </Typography>
                                                    {(team.memberCount ?? 0) > 0 && (
                                                        <Typography
                                                            variant="caption"
                                                            sx={{
                                                                color: isDarkMode ? '#64748b' : '#94a3b8',
                                                            }}
                                                        >
                                                            {team.memberCount} members
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </Box>
                                        </TableCell>
                                        <TableCell
                                            align="center"
                                            sx={{
                                                borderBottom: `1px solid ${isDarkMode ? '#334155' : '#e2e8f0'}`,
                                            }}
                                        >
                                            <Chip
                                                label={`${team.weight}%`}
                                                size="small"
                                                sx={{
                                                    bgcolor: isDarkMode ? '#334155' : '#e2e8f0',
                                                    color: isDarkMode ? '#f1f5f9' : '#1e293b',
                                                    fontWeight: 700,
                                                    fontSize: '0.8rem',
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell
                                            align="center"
                                            sx={{
                                                borderBottom: `1px solid ${isDarkMode ? '#334155' : '#e2e8f0'}`,
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    display: 'inline-flex',
                                                    bgcolor: scoreColor,
                                                    color: '#fff',
                                                    px: 1.5,
                                                    py: 0.5,
                                                    borderRadius: 2,
                                                    fontWeight: 700,
                                                    fontSize: '0.85rem',
                                                    boxShadow: `0 2px 6px ${scoreColor}40`,
                                                }}
                                            >
                                                {Math.round(team.score)}%
                                            </Box>
                                        </TableCell>
                                        <TableCell
                                            align="center"
                                            sx={{
                                                borderBottom: `1px solid ${isDarkMode ? '#334155' : '#e2e8f0'}`,
                                            }}
                                        >
                                            <Chip
                                                label={team.status || 'In Progress'}
                                                size="small"
                                                sx={{
                                                    bgcolor: `${scoreColor}15`,
                                                    color: scoreColor,
                                                    fontWeight: 600,
                                                    fontSize: '0.75rem',
                                                    border: `1px solid ${scoreColor}30`,
                                                }}
                                            />
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </CollapsibleCard>
    );
};

export default StrategicUnitsCard;
